// API client for the uplevate-ai SPA. Same origin, cookie session — nothing stored in
// localStorage except the theme. Pages import these and call them from Python.

const BASE = '/api';

async function req(method, path, body) {
  const init = { method, headers: {}, credentials: 'same-origin' };
  if (body !== undefined && body !== null) {
    init.headers['Content-Type'] = 'application/json';
    init.body = JSON.stringify(body);
  }
  const resp = await fetch(BASE + path, init);
  const ct = resp.headers.get('content-type') || '';
  const data = ct.includes('application/json') ? await resp.json() : await resp.text();
  if (resp.status === 401) { window.location.href = '/api/auth/login?next=' + encodeURIComponent(location.pathname); throw new Error('sign in'); }
  if (!resp.ok) {
    const err = new Error((data && data.detail) ? data.detail : ('HTTP ' + resp.status));
    err.status = resp.status; err.data = data; err.code = (data && data.code) || null; throw err;
  }
  return data;
}

// ---- identity / workspace ---------------------------------------------------
export const getMe = () => req('GET', '/me');
export const logout = async () => { const r = await req('POST', '/auth/logout'); window.location.href = r.redirect || '/'; };
export const getWorkspace = () => req('GET', '/workspace');
export const wakeWorkspace = () => req('POST', '/workspace/wake');
export const sleepWorkspace = () => req('POST', '/workspace/sleep');
export const setKeepAwake = (on) => req('POST', '/workspace/keep_awake', { on: !!on });
export const getUsage = () => req('GET', '/workspace/usage');

// ---- workspaces (several per person; some shared with them) ------------------
export const listWorkspaces = () => req('GET', '/workspaces');
export const createWorkspace = (name) => req('POST', '/workspaces', { name });
export const workspaceState = (id) => req('GET', '/workspaces/' + id);
export const renameWorkspace = (id, name) => req('PATCH', '/workspaces/' + id, { name });
export const deleteWorkspace = (id) => req('DELETE', '/workspaces/' + id);
export const wakeWs = (id) => req('POST', '/workspaces/' + id + '/wake');
export const sleepWs = (id) => req('POST', '/workspaces/' + id + '/sleep');
export const keepAwakeWs = (id, on) => req('POST', '/workspaces/' + id + '/keep_awake', { on: !!on });
export const usageWs = (id) => req('GET', '/workspaces/' + id + '/usage');
export const listMembers = (id) => req('GET', '/workspaces/' + id + '/members');
export const inviteMember = (id, email, role) => req('POST', '/workspaces/' + id + '/members', { email, role });
export const setMemberRole = (id, mid, role) => req('PATCH', '/workspaces/' + id + '/members/' + mid, { role });
export const removeMember = (id, mid) => req('DELETE', '/workspaces/' + id + '/members/' + mid);
export const acceptInvite = (token) => req('POST', '/invites/' + encodeURIComponent(token) + '/accept');

// ---- prepaid credit (hosting + AI), the trial, top-ups ----------------------
export const getWallet = () => req('GET', '/wallet');
export const walletHistory = () => req('GET', '/wallet/history');
export const startTopup = (pocket, amountCents) => req('POST', '/wallet/topup', { pocket, amount_cents: amountCents });
export const getOrder = (id) => req('GET', '/wallet/order/' + id);

// ---- projects (chats) -------------------------------------------------------
export const listProjects = (workspace, archived) =>
  req('GET', '/projects?workspace=' + (workspace || '') + (archived ? '&archived=1' : ''));
export const sharedProjects = () => req('GET', '/projects/shared');
export const createProject = (title, mode, workspace) => req('POST', '/projects', { title, mode: mode || 'auto', workspace });
export const restoreProject = (id) => req('POST', '/projects/' + id + '/restore');
export const deleteProject = (id, withFiles) => req('DELETE', '/projects/' + id + '?files=' + (withFiles ? 'delete' : 'keep'));

// ---- sharing one chat -------------------------------------------------------
export const shareLinks = (id) => req('GET', '/projects/' + id + '/share-link');
export const createShareLink = (id) => req('POST', '/projects/' + id + '/share-link');
export const revokeShareLink = (id, token) => req('DELETE', '/projects/' + id + '/share-link/' + token);
export const chatPeople = (id) => req('GET', '/projects/' + id + '/access');
export const addChatPerson = (id, email) => req('POST', '/projects/' + id + '/access', { email });
export const removeChatPerson = (id, aid) => req('DELETE', '/projects/' + id + '/access/' + aid);
export async function publicSnapshot(token) {
  const r = await fetch(BASE + '/s/' + encodeURIComponent(token));
  const d = await r.json();
  if (!r.ok) throw new Error(d.detail || ('HTTP ' + r.status));
  return d;
}
export const getProject = (id) => req('GET', '/projects/' + id);
export const updateProject = (id, fields) => req('PATCH', '/projects/' + id, fields);
export const archiveProject = (id) => req('POST', '/projects/' + id + '/archive');
export const listFiles = (id) => req('GET', '/projects/' + id + '/files');
export const readFile = (id, path) => req('GET', '/projects/' + id + '/file?path=' + encodeURIComponent(path));

// ---- chat -------------------------------------------------------------------
export const sendMessage = (id, text, attachments) => req('POST', '/projects/' + id + '/messages', { text, attachments: attachments || [] });
export const answerCallback = (id, payload) => req('POST', '/projects/' + id + '/answer', payload);
export const cancelTurn = (id) => req('POST', '/projects/' + id + '/cancel');

// ---- keys -------------------------------------------------------------------
export const listKeys = () => req('GET', '/keys');
export const createKey = (name, scopes) => req('POST', '/keys', { name, scopes });
export const revokeKey = (id) => req('DELETE', '/keys/' + id);

// ---- live stream ------------------------------------------------------------
// Opens the project's SSE stream (data-only frames carrying {type, seq, ...}) and calls
// onEvent for each. Returns a closer. Reconnects itself from the last seq on drop.
export function streamProject(id, after, onEvent, onStatus) {
  let es = null, last = after || 0, closed = false;
  function open() {
    es = new EventSource(BASE + '/projects/' + id + '/stream?after=' + last, { withCredentials: true });
    es.onmessage = (e) => {
      let ev; try { ev = JSON.parse(e.data); } catch (err) { return; }
      if (ev.seq) last = ev.seq;
      onEvent(ev);
    };
    es.onopen = () => onStatus && onStatus('open');
    es.onerror = () => { onStatus && onStatus('reconnecting'); es.close(); if (!closed) setTimeout(open, 1500); };
  }
  open();
  return () => { closed = true; if (es) es.close(); };
}

export function currentTheme() { return document.documentElement.getAttribute('data-theme') || 'dark'; }
export function toggleTheme() {
  const t = currentTheme() === 'dark' ? 'light' : 'dark';
  document.documentElement.setAttribute('data-theme', t);
  try { localStorage.setItem('theme', t); } catch (e) {}
  return t;
}
